import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormHandles } from '@unform/core';
import { Form } from '../../../forms';
import { useRef } from 'react';
import * as Yup from 'yup';
import { VTextField, VTextFieldPhone } from '../../../forms';
import getValidationErrors from '../../../helpers/getValidationErrors';
import { authService, feedback } from '../../../services';
import { Button } from '../../MUI/button/Button';
import { Api } from '../../../services/api/axios-config';
import { setCsrfRequestToken } from '../../../services/api/axios-config/interceptors';

interface IProps {
  open: boolean;
  businessId: number;
  onClose: () => void;
}

const schema = Yup.object().shape({
  name: Yup.string().required('Nome é obrigatório'),
  email: Yup.string().email('E-mail inválido').nullable(),
  phone: Yup.string().nullable(),
});

export const CreateClientModal: React.FC<IProps> = ({ open, businessId, onClose }) => {
  const formRef = useRef<FormHandles>(null);
  const queryClient = useQueryClient();

  const { mutate: createClient, isLoading } = useMutation({
    mutationFn: async (data: { name: string; email: string; phone: string }) => {
      // Refresh CSRF token before creating a client — token may be stale
      // after a long session or login flow (e.g. receptionist invite → login)
      try {
        const tokenRes = await Api.get('/auth/antiforgery-token');
        if (tokenRes.data) setCsrfRequestToken(tokenRes.data as string);
      } catch {
        // best-effort — the interceptor will use the stored token if available
      }
      return authService.createClient({
        name: data.name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        businessId,
      });
    },
    onSuccess: () => {
      feedback('Cliente cadastrado com sucesso!', 'success');
      queryClient.invalidateQueries({ queryKey: ['clients', businessId] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      onClose();
    },
    onError: (error: Error) => feedback(String(error), 'error'),
  });

  const handleSubmit = async (data: { name: string; email: string; phone: string }) => {
    try {
      formRef.current?.setErrors({});
      await schema.validate(data, { abortEarly: false });
      createClient(data);
    } catch (err: unknown) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        formRef.current?.setErrors(errors);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant='h6'>Novo cliente</Typography>
        <IconButton onClick={onClose} size='small'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Form ref={formRef} onSubmit={handleSubmit} initialData={{ phone: '', email: '' }}>
          <Box display='flex' flexDirection='column' gap={2} mt={1}>
            <VTextField name='name' label='Nome completo' required fullWidth />
            <VTextField name='email' label='E-mail' type='email' fullWidth />
            <VTextFieldPhone name='phone' label='Telefone' fullWidth />

            <Box display='flex' justifyContent='flex-end' gap={1} mt={1}>
              <Button label='Cancelar' variant='outlined' onClick={onClose} />
              <Button label='Salvar' variant='contained' type='submit' isLoading={isLoading} />
            </Box>
          </Box>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
